import { toast } from 'sonner'
import type { MouseEvent } from 'react'

export type ToastType = 'success' | 'warning' | 'info' | 'error'

export interface ToastOptions {
  description?: string
  duration?: number
  action?: {
    label: string
    onClick: (event: MouseEvent<HTMLButtonElement>) => void
  }
  cancel?: {
    label: string
    onClick: (event: MouseEvent<HTMLButtonElement>) => void
  }
}

class ToastService {
  /**
   * Show a success toast message
   */
  success(message: string, options?: ToastOptions) {
    return toast.success(message, {
      description: options?.description,
      duration: options?.duration,
      action: options?.action,
      cancel: options?.cancel,
    })
  }

  /**
   * Show a warning toast message
   */
  warning(message: string, options?: ToastOptions) {
    return toast.warning(message, {
      description: options?.description,
      duration: options?.duration,
      action: options?.action,
      cancel: options?.cancel,
    })
  }

  /**
   * Show an info toast message
   */
  info(message: string, options?: ToastOptions) {
    return toast.info(message, {
      description: options?.description,
      duration: options?.duration,
      action: options?.action,
      cancel: options?.cancel,
    })
  }

  /**
   * Show an error toast message
   */
  error(message: string, options?: ToastOptions) {
    return toast.error(message, {
      description: options?.description,
      duration: options?.duration,
      action: options?.action,
      cancel: options?.cancel,
    })
  }

  /**
   * Show a loading toast message
   */
  loading(message: string, options?: Omit<ToastOptions, 'action' | 'cancel'>) {
    return toast.loading(message, {
      description: options?.description,
      duration: options?.duration,
    })
  }

  /**
   * Dismiss a toast by its ID
   */
  dismiss(toastId: string | number) {
    toast.dismiss(toastId)
  }

  /**
   * Dismiss all toasts
   */
  dismissAll() {
    toast.dismiss()
  }

  /**
   * Show a promise toast that automatically updates based on promise state
   */
  promise<T>(
    promise: Promise<T>,
    messages: {
      loading: string
      success: string | ((data: T) => string)
      error: string | ((error: any) => string)
    },
    options?: Omit<ToastOptions, 'description' | 'action' | 'cancel'>
  ) {
    return toast.promise(promise, {
      loading: messages.loading,
      success: messages.success,
      error: messages.error,
      duration: options?.duration,
    })
  }
}

// Export a singleton instance
export const toastService = new ToastService()

// Also export individual functions for convenience
export const showToast = {
  success: (message: string, options?: ToastOptions) => toastService.success(message, options),
  warning: (message: string, options?: ToastOptions) => toastService.warning(message, options),
  info: (message: string, options?: ToastOptions) => toastService.info(message, options),
  error: (message: string, options?: ToastOptions) => toastService.error(message, options),
  loading: (message: string, options?: Omit<ToastOptions, 'action' | 'cancel'>) =>
    toastService.loading(message, options),
  dismiss: (toastId: string | number) => toastService.dismiss(toastId),
  dismissAll: () => toastService.dismissAll(),
  promise: <T>(
    promise: Promise<T>,
    messages: {
      loading: string
      success: string | ((data: T) => string)
      error: string | ((error: any) => string)
    },
    options?: Omit<ToastOptions, 'description' | 'action' | 'cancel'>
  ) => toastService.promise(promise, messages, options),
}

